import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { CategoryTabs } from "@/components/CategoryTabs";
import { SourceFilters } from "@/components/SourceFilters";
import { HeroCard } from "@/components/HeroCard";
import { HeroCarousel } from "@/components/HeroCarousel";
import { EventBanner } from "@/components/EventBanner";
import { LensStoryOriginals } from "@/components/LensStoryOriginals";
import { VideoCard } from "@/components/VideoCard";
import { ShortCard } from "@/components/ShortCard";
import { SearchOverlay } from "@/components/SearchOverlay";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";

const ALL_CATEGORIES = ["All", "BanglaTok", "Music", "News", "Breaking News", "Natok", "Drama", "Food", "Travel"];

export default function Home() {
  const [category, setCategory] = useState("All");
  const [source, setSource] = useState("All");
  const [trending, setTrending] = useState({ hero: null, items: [] });
  const [shorts, setShorts] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  // Load trending hero + horizontal row (only on All category)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [{ data: t }, { data: b }] = await Promise.all([
          api.get("/trending"),
          api.get("/banglatok", { params: { limit: 9 } }),
        ]);
        if (!active) return;
        setTrending(t || { hero: null, items: [] });
        setShorts(b.items || []);
      } catch {/* ignore */}
    })();
    return () => { active = false; };
  }, []);

  // Load category feed
  useEffect(() => {
    let active = true;
    // For "All" with no source filter, reuse trending.items to avoid a duplicate fetch
    if (category === "All" && source === "All" && trending.items && trending.items.length) {
      setFeed(trending.items);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        if (category === "BanglaTok") {
          const { data } = await api.get("/banglatok", { params: { limit: 18 } });
          if (active) setFeed(data.items || []);
        } else {
          const { data } = await api.get("/videos", { params: { category, source, limit: 24 } });
          if (active) setFeed(data.items || []);
        }
      } catch {
        if (active) setFeed([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [category, source, trending.items]);

  const showHero = category === "All";

  return (
    <>
      <Header onSearch={() => setSearchOpen(true)} />

      <CategoryTabs categories={ALL_CATEGORIES} active={category} onChange={setCategory} />

      {showHero && (
        <HeroCarousel>
          <EventBanner />
          <HeroCard video={trending.hero} loading={!trending.hero && loading} />
        </HeroCarousel>
      )}

      {/* LensStory Originals — premium studio section right under hero */}
      {showHero && <LensStoryOriginals />}

      {showHero && <SourceFilters active={source} onChange={setSource} />}

      {/* Trending row */}
      {showHero && trending.items.length > 0 && (
        <section className="mt-6">
          <SectionHeader title="Trending this week" />
          <div className="flex overflow-x-auto scrollbar-hide gap-4 px-4 pb-4 snap-x">
            {trending.items.slice(1, 9).map((v) => (
              <VideoCard key={v.video_id} video={v} />
            ))}
          </div>
        </section>
      )}

      {/* BanglaTok preview row */}
      {showHero && shorts.length > 0 && (
        <section className="mt-2">
          <SectionHeader title="BanglaTok" to="/banglatok" />
          <div className="flex overflow-x-auto scrollbar-hide gap-3 px-4 pb-4 snap-x">
            {shorts.slice(0, 9).map((v) => (
              <div key={v.video_id} className="min-w-[140px] snap-start">
                <ShortCard video={v} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Category feed (when not on All) */}
      {!showHero && (
        <section className="mt-2">
          <SectionHeader title={`${category} videos`} />
          {loading ? (
            <LoadingGrid />
          ) : feed.length === 0 ? (
            <EmptyFeed />
          ) : category === "BanglaTok" ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 px-4">
              {feed.map((v) => <ShortCard key={v.video_id} video={v} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 px-4">
              {feed.map((v) => <VideoCard key={v.video_id} video={v} variant="full" />)}
            </div>
          )}
        </section>
      )}

      {/* If on All, also show grid of more videos under */}
      {showHero && (
        <section className="mt-4">
          <SectionHeader title="More for you" />
          {loading ? (
            <LoadingGrid />
          ) : (
            <div className="grid grid-cols-1 gap-5 px-4">
              {feed.slice(0, 10).map((v) => <VideoCard key={v.video_id} video={v} variant="full" />)}
            </div>
          )}
        </section>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

function SectionHeader({ title, to }) {
  return (
    <div className="flex items-center justify-between px-4 pb-3">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
        <span className="w-1 h-5 rounded-full bg-brand" />
        {title}
      </h2>
      {to && (
        <Link to={to} className="text-brand text-xs font-medium inline-flex items-center gap-1 hover:underline">
          See all <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}

function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 gap-5 px-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="aspect-video rounded-xl bg-bg-surface animate-pulse" />
      ))}
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="px-4 py-12 text-center text-white/40 text-sm">
      <Loader2 size={18} className="inline-block animate-spin mr-2" /> Loading content…
    </div>
  );
}
